'use client';

import {onEmitClickEvent} from '@/lib/utils/analytics';
import {SyntheticEvent} from 'react';
import {useSearchStoreDispatch} from '@/hooks/catalog';
import {
	ButtonFieldsFragment,
	CatalogOfferCardVariant,
	EdsButtonVariant,
	EdsSize
} from '@common/hygraph-schema';
import {ButtonProps, Button as EdsButton, Link} from '@eds-web/react';
import {useAccountStore, useCountry} from '@/hooks';
import {AnalyticEventProps} from '@/types';
import {Grid, GridItem} from '@panda/jsx';
import {CatalogOfferCard} from '../catalog-offer-card';
import {useLocale} from '@epic-mw/i18n';
import {getEdsIcon} from '@/lib/utils/edsIcon.utils';

const emitEvent = (
	event: SyntheticEvent<HTMLElement> | Event | null | undefined,
	analyticsProps?: AnalyticEventProps
) => {
	onEmitClickEvent({
		eventCategory: 'button',
		interactionType: 'click',
		eventAction: 'loadMore',
		eventLabel: 'Load More Products',
		...analyticsProps
	})(event);
};

export interface LoadMoreButtonProps
	extends ButtonFieldsFragment,
		Omit<ButtonProps, 'size' | 'icon' | 'iconPosition'> {
	sandboxId?: string;
	analytics?: AnalyticEventProps;
}

export const LoadMoreButton = ({
	__typename,
	sandboxId = 'fn',
	analytics,
	ukey,
	size,
	btnLabel,
	btnHref,
	btnVariant,
	icon,
	iconPosition,
	...edsProps
}: LoadMoreButtonProps) => {
	const {isLoading: accountLoading} = useAccountStore();
	const countryCode = useCountry();
	const locale = useLocale();

	const more = useSearchStoreDispatch('loadmore', {
		onSuccess: () => {
			emitEvent(null, {
				...analytics,
				eventValue: {
					status: 'success',
					sandboxId,
					country: countryCode
				}
			});
		},
		onError: (error) => {
			emitEvent(null, {
				...analytics,
				eventValue: {
					status: 'error',
					error,
					sandboxId,
					country: countryCode
				}
			});
		}
	});

	const isLoading = more.isMutating || accountLoading;

	const btnProps: ButtonProps = {
		...edsProps,
		size: size ?? EdsSize.Md,
		variant: btnVariant ?? EdsButtonVariant.Cta,
		icon: getEdsIcon(icon),
		iconPosition: iconPosition ?? undefined,
		loading: isLoading,
		disabled: isLoading,
		loadingLabel: btnLabel,
		children: btnLabel,
		style: {alignSelf: 'center'}
	};

	if (typeof btnHref === 'string' && btnHref) {
		return <EdsButton key={ukey} {...btnProps} as={Link} href={btnHref} />;
	}

	if (more.data?.length) {
		return (
			<Grid gap={4} gridTemplateColumns={{base: '1fr 1fr 1fr 1fr', smDown: '1fr 1fr'}}>
				{more.data.map((card, i) => (
					<GridItem key={`${card.title}-${i}`} h="full">
						<CatalogOfferCard
							{...card}
							variant={CatalogOfferCardVariant.Outline}
							isLoading={isLoading}
							analytics={analytics}
							locale={locale}
						/>
					</GridItem>
				))}
			</Grid>
		);
	}

	return (
		<EdsButton
			key={ukey}
			{...btnProps}
			onClick={(e) => {
				if (isLoading) return;

				const apiPayload = {sandboxId, country: countryCode};

				emitEvent(e, {
					...analytics,
					eventValue: {
						status: 'initiate',
						...apiPayload
					}
				});

				more.trigger(apiPayload).catch((err) => {
					console.error('Load More Button:', err);
				});
			}}
		/>
	);
};
